import { FC } from 'react'

import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { LoadingOverlay, Text } from '@mantine/core'

import { device } from 'src/Helpers/constants.ts'
import { PageTitle } from 'src/Components/PageTitle/index.tsx'
import { Stepper } from 'src/Components/Stepper'
import { RoutesEnum } from 'src/Router/types.ts'

import { useCreateContract } from './useCreateContract'
import { ContractFormStep } from './ContractFormStep'
import { EquipmentSelectionStep } from './EquipmentSelectionStep'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 36px;
  position: relative;
  height: 100%;
  overflow: hidden;
  min-width: 800px;

  @media ${device.tabletMax} {
    gap: 20px;
  }
`

const LoadingMessage = styled(Text)`
  margin-top: 80px;
  font-size: 16px;
  font-weight: 500;
  color: #228be6;
`

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 50px;
  position: static;

  @media ${device.tabletMax} {
    gap: 20px;
  }
`

const InnerContainer = styled.div`
  height: 100%;
  width: 100%;
  overflow: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 36px;
`

export const CreateContractPage: FC = () => {
  const navigate = useNavigate()
  const {
    control,
    errors,
    offer,
    handleSubmit,
    onSubmit,
    handleCancel,
    setValue,
    selectedEquipments,
    onEquipmentSelect,
    onWorkTypeChange,
    isGeneratingDocument,
    onShiftsCountChange,
    onMinHoursPerShiftChange,
    isEditClientModalOpen,
    openEditClientModal,
    closeEditClientModal,
    handleUpdateClient,
  } = useCreateContract()

  const steps = [
    {
      id: 1,
      title: 'Данные для контракта',
      content: (
        <ContractFormStep
          control={control}
          errors={errors}
          offer={offer}
          onSubmit={handleSubmit(onSubmit)}
          onCancel={handleCancel}
          setValue={setValue}
          isEditClientModalOpen={isEditClientModalOpen}
          openEditClientModal={openEditClientModal}
          closeEditClientModal={closeEditClientModal}
          handleUpdateClient={handleUpdateClient}
        />
      ),
    },
    {
      id: 2,
      title: 'Выбор транспортных средств',
      content: (
        <EquipmentSelectionStep
          offer={offer}
          selectedEquipments={selectedEquipments}
          onEquipmentSelect={onEquipmentSelect}
          onWorkTypeChange={onWorkTypeChange}
          onShiftsCountChange={onShiftsCountChange}
          onMinHoursPerShiftChange={onMinHoursPerShiftChange}
        />
      ),
    },
  ]

  const onStepClick = (stepId: number): void => {
    if (stepId === 1) {
      navigate(RoutesEnum.CONTRACTS)
    }
  }

  return (
    <Container>
      <LoadingOverlay
        visible={isGeneratingDocument}
        overlayProps={{ blur: 2 }}
        loaderProps={{ color: 'blue', type: 'dots', size: 'lg' }}
      >
        <LoadingMessage>Генерация документа договора...</LoadingMessage>
      </LoadingOverlay>
      <TitleWrapper>
        <PageTitle>Создание контракта</PageTitle>
      </TitleWrapper>
      <InnerContainer>
        <Stepper
          steps={steps}
          onStepClick={onStepClick}
          onFinish={handleSubmit(onSubmit)}
        />
      </InnerContainer>
    </Container>
  )
}
